/**
 * `YYYY-MM-DD` calendar keys — the shape of a board's script finish date
 * (F-135) and of rainvic task deadlines. A key is a LOCAL calendar day with
 * no time zone, so it is never parsed with `new Date(key)` (which reads it
 * as UTC midnight and shows the day before, west of Greenwich).
 */

/** Today, as a local `YYYY-MM-DD` key. */
export function todayDateKey(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Is the key strictly before today? Keys compare lexically by design. */
export function isPastDateKey(key: string, now = new Date()): boolean {
  return key < todayDateKey(now);
}

/** "Sep 30" / "Sep 30, 2027" — the year only when it is not this one. */
export function formatDateKey(key: string, now = new Date()): string {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return key;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return key;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(y !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}

/**
 * "Mon Sep 21" — the weekday first, for the dates of a Shorts week (F-140),
 * which are days of the week before they are days of the month: the shoot is
 * "Monday", the releases are "Monday to Friday", and a chip that said only
 * "Sep 21" would make the reader count on their fingers.
 */
export function formatDateKeyWithWeekday(key: string, now = new Date()): string {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return key;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return key;
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(y !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}
